// Trial mode store. Persists a single JSON envelope in localStorage that
// emulates the rows the app would have in Supabase if it were authed.
// Backed by MockSupabase so the rest of the codebase calls supabase normally.

import { getDefaultProjectData } from "./DefaultProjectData";

const STORAGE_KEY = "peepz.trial.v1";
const PROJECT_TYPE = "video_game";

// Default day types mirror the seed in supabase/migrations/20241012135943_daytypes_member_working.sql
// (id values are local, but role_required/style/system fields match the production seed)
const DEFAULT_DAY_TYPES = [
  { name: "Working", style: "working", role_required: "member", description: "Person normally available.", system: true },
  { name: "Sick", style: "sick", role_required: "member", description: "Person unavailable, poor thing out sick :(", system: false },
  { name: "Vacation", style: "vacation", role_required: "member", description: "Person unavailable, hopefully sipping mojitos on a beach!", system: false },
  { name: "Parental", style: "parental", role_required: "member", description: "Person unavailable, taking care of the little ones.", system: false },
  { name: "Local holiday", style: "holiday", role_required: "manager", description: "Person unavailable due to local holiday in the country.", system: false },
];

function nowIso() {
  return new Date().toISOString();
}

function todayDateOnly() {
  return new Date().toISOString().split("T")[0];
}

function nextWeekdayDateOnly() {
  const d = new Date();
  const day = d.getDay();
  if (day === 0) d.setDate(d.getDate() + 1);
  else if (day === 6) d.setDate(d.getDate() + 2);
  return d.toISOString().split("T")[0];
}

function emptyEnvelope() {
  return {
    version: 1,
    createdAt: nowIso(),
    nextId: 1,
    user: null,
    orgs: [],
    projects: [],
    memberships: [],
    people: [],
    days: [],
    day_types: [],
    goals: [],
    settings: [],
    work: [],
    workitems: [],
    stories: [],
    images: [],
    uploads: {},
    onboarding: { welcomeDismissed: false },
  };
}

export function loadTrialStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveTrialStore(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    return { ok: true };
  } catch (err) {
    if (err && (err.name === "QuotaExceededError" || err.code === 22)) {
      return { ok: false, code: "QUOTA" };
    }
    return { ok: false, code: "UNKNOWN", error: err };
  }
}

export function clearTrialStore() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function trialExists() {
  return loadTrialStore() !== null;
}

export function nextId(state) {
  const id = state.nextId || 1;
  state.nextId = id + 1;
  return id;
}

// Build a fresh envelope with the seed_self shape: one org, one project, one
// "You" person row joining today, default day_types, an unplanned goal, and
// default disciplines stored as a project-wide settings row.
export function bootstrapTrialStore() {
  const state = emptyEnvelope();

  const orgId = nextId(state);
  const projectId = nextId(state);
  const personId = nextId(state);
  const membershipId = nextId(state);

  state.orgs.push({
    id: orgId,
    created_at: nowIso(),
    name: "Your Org",
    status: null,
    image_id: null,
  });

  state.projects.push({
    id: projectId,
    created_at: nowIso(),
    name: "Your Project",
    org_id: orgId,
    description: null,
    status: null,
    type: PROJECT_TYPE,
  });

  state.people.push({
    id: personId,
    created: nowIso(),
    firstname: "You",
    lastname: "",
    email: null,
    uid: null,
    title: null,
    imageid: null,
    fullname: null,
    discipline_id: null,
    current_org_id: orgId,
    current_proj_id: projectId,
  });

  state.memberships.push({
    id: membershipId,
    created_at: nowIso(),
    person_id: personId,
    org_id: orgId,
    type: "owner",
    status: "active",
    project_invite: null,
  });

  for (const dt of DEFAULT_DAY_TYPES) {
    state.day_types.push({
      id: nextId(state),
      created_at: nowIso(),
      org_id: orgId,
      ...dt,
    });
  }

  state.days.push({
    id: nextId(state),
    created_at: nowIso(),
    person_id: personId,
    project_id: projectId,
    date: nextWeekdayDateOnly(),
    system_type: "joins",
    day_type: null,
    comment: null,
  });

  const unplannedGoalId = nextId(state);
  state.goals.push({
    id: unplannedGoalId,
    created_at: nowIso(),
    project_id: projectId,
    type: "unplanned",
    name: null,
    body: null,
    date: null,
    color: null,
    workitems_order: [],
    stories_order: null,
    private: false,
  });

  state.settings.push({
    id: nextId(state),
    created_at: nowIso(),
    project_id: projectId,
    user_id: null,
    name: "project_disciplines",
    value: JSON.stringify(getDefaultProjectData(PROJECT_TYPE).map(({ doDs, ...rest }) => rest)),
  });

  state.user = { id: personId, current_org_id: orgId, current_proj_id: projectId };

  saveTrialStore(state);
  return state;
}

// Build a "user" object in the same shape Timeline and other pages expect from
// the real AuthContext getSession() hydration. This lets the existing
// /auth/* routing render the trial user as if they were authed.
export function buildFakeUserFromTrial(state) {
  const person = state.people.find((p) => p.id === state.user.id) || state.people[0];
  const org = state.orgs.find((o) => o.id === state.user.current_org_id) || state.orgs[0];
  const project = state.projects.find((p) => p.id === state.user.current_proj_id) || state.projects[0];

  // Memberships expanded with embedded orgs + people (with days), matching the
  // shape returned by `memberships.select("*, orgs(*), people(*, days(*))")`
  const memberships = state.memberships
    .filter((m) => m.person_id === person.id)
    .map((m) => {
      const orgRow = state.orgs.find((o) => o.id === m.org_id);
      const personRow = state.people.find((p) => p.id === m.person_id);
      const days = state.days.filter((d) => d.person_id === m.person_id);
      return { ...m, orgs: orgRow ? { ...orgRow } : null, people: personRow ? { ...personRow, days } : null };
    });

  // Project settings (per-user and project-wide), matching the .or() filter
  // used in AuthContext that loads `*, settings(*)` filtered to this user.
  const settings = state.settings.filter(
    (s) => s.project_id === project.id && (s.user_id === null || s.user_id === undefined || s.user_id === person.id)
  );

  const projects = state.projects
    .filter((p) => p.org_id === org.id)
    .map((p) => {
      const projSettings = state.settings.filter(
        (s) => s.project_id === p.id && (s.user_id === null || s.user_id === undefined || s.user_id === person.id)
      );
      return { ...p, settings: projSettings };
    });

  // Try to surface the user's stored avatar as a data URL (matches what
  // MockSupabase's createSignedUrl would return).
  let avatar = null;
  const avatarRow = state.images.find((img) => img.ownerid === person.id && img.type === "avatar");
  if (avatarRow) {
    const entry = state.uploads[`avatars/${avatarRow.url}`];
    avatar = entry?.dataUrl || null;
  }
  let orgLogo = null;
  const logoRow = state.images.find((img) => img.org_id === org.id && img.type === "logo");
  if (logoRow) {
    const entry = state.uploads[`logos/${logoRow.url}`];
    orgLogo = entry?.dataUrl || null;
  }

  return {
    ...person,
    id: person.id,
    access_token: null,
    avatar,
    memberships: memberships.length ? memberships : null,
    org,
    proj: { ...project, settings },
    projects,
    created: state.createdAt,
    last_signin: state.createdAt,
    role: memberships.find((m) => m.org_id === org.id)?.type || "owner",
    orgLogo,
  };
}

// Append a small sample team. Used by the welcome popup's "Add a sample team"
// action. Adds 4 placeholder people with varied join dates plus one milestone
// goal so the timeline has something to play with on first paint.
export function addSampleTeam(state) {
  const projectId = state.user.current_proj_id;
  const orgId = state.user.current_org_id;
  const today = new Date();

  const samples = [
    { firstname: "Alex", lastname: "Chen", joinsDelta: -20 },
    { firstname: "Priya", lastname: "Sharma", joinsDelta: -10 },
    { firstname: "Mia", lastname: "Lopez", joinsDelta: -5 },
    { firstname: "Sam", lastname: "Okafor", joinsDelta: 0 },
  ];

  for (const s of samples) {
    const pid = nextId(state);
    state.people.push({
      id: pid,
      created: nowIso(),
      firstname: s.firstname,
      lastname: s.lastname,
      email: null,
      uid: null,
      title: null,
      imageid: null,
      discipline_id: null,
      current_org_id: orgId,
      current_proj_id: projectId,
    });
    state.memberships.push({
      id: nextId(state),
      created_at: nowIso(),
      person_id: pid,
      org_id: orgId,
      type: "member",
      status: "placeholder",
      project_invite: null,
    });
    const joinDate = new Date(today);
    joinDate.setDate(joinDate.getDate() + s.joinsDelta);
    state.days.push({
      id: nextId(state),
      created_at: nowIso(),
      person_id: pid,
      project_id: projectId,
      date: joinDate.toISOString().split("T")[0],
      system_type: "joins",
      day_type: null,
      comment: null,
    });
  }

  // One sample milestone two weeks out
  const milestoneDate = new Date(today);
  milestoneDate.setDate(milestoneDate.getDate() + 14);
  state.goals.push({
    id: nextId(state),
    created_at: nowIso(),
    project_id: projectId,
    type: "minor",
    name: "First playable demo",
    body: "Aim for a runnable build the whole team can poke at.",
    date: milestoneDate.toISOString().split("T")[0],
    color: null,
    workitems_order: [],
    stories_order: null,
    private: false,
  });

  state.onboarding.welcomeDismissed = true;
  saveTrialStore(state);
  return state;
}

export function setOnboardingFlag(state, key, value) {
  state.onboarding[key] = value;
  saveTrialStore(state);
}

export { STORAGE_KEY, todayDateOnly };
